/**
 * Billing Period Utilities
 * Centralized mappings and helper functions for billing period conversions
 */

/**
 * Maps frontend tab values to backend API values
 * @type {Object.<string, string>}
 */
export const billingPeriodMap = {
  monthly: 'monthly',
  quarterly: 'quarterly',
  semi_annual: 'semi_annual',
  annual: 'annual',
  two_year: 'two_year',
};

/**
 * Maps backend API values to frontend tab values (inverse of billingPeriodMap)
 * @type {Object.<string, string>}
 */
export const backendToFrontendPeriodMap = {
  monthly: 'monthly',
  quarterly: 'quarterly',
  semi_annual: 'semi_annual',
  annual: 'annual',
  two_year: 'two_year',
};

/**
 * Maps billing periods to human-readable display text
 * @type {Object.<string, string>}
 */
export const billingPeriodDisplayText = {
  monthly: 'month',
  quarterly: 'quarter',
  semi_annual: '6 months',
  annual: 'year',
  two_year: '2 years',
};

/**
 * Maps billing periods to frequency text (e.g., 'yearly', 'quarterly', 'monthly')
 * @type {Object.<string, string>}
 */
export const billingPeriodFrequencyText = {
  monthly: 'monthly',
  quarterly: 'quarterly',
  semi_annual: 'semi-annually',
  annual: 'yearly',
  two_year: 'every 2 years',
};

/**
 * Maps billing periods to suffix text for credit displays
 * @type {Object.<string, string>}
 */
export const billingPeriodSuffix = {
  monthly: '/mo',
  quarterly: '/qtr',
  semi_annual: '/6mo',
  annual: '/yr',
  two_year: '/2yr',
};

/**
 * Maps billing periods to "Billed" label text
 * @type {Object.<string, string>}
 */
export const billedLabelMap = {
  month: 'Billed Monthly',
  quarter: 'Billed Quarterly',
  semi_annual: 'Billed Semi-Annually',
  annual: 'Billed Annually',
  two_year: 'Billed Every 2 Years',
};

/**
 * Convert frontend tab value to backend API value
 * @param {string} frontendPeriod - Frontend tab value (e.g., 'annual', 'two_year')
 * @returns {string} Backend API value (e.g., 'annual', 'two_year')
 */
export function toBackendPeriod(frontendPeriod) {
  return billingPeriodMap[frontendPeriod] || 'monthly';
}

/**
 * Convert backend API value to frontend tab value
 * @param {string} backendPeriod - Backend API value (e.g., 'annual', 'two_year')
 * @returns {string} Frontend tab value (e.g., 'annual', 'two_year')
 */
export function toFrontendPeriod(backendPeriod) {
  return backendToFrontendPeriodMap[backendPeriod] || 'annual';
}

/**
 * Get display text for a billing period
 * @param {string} period - Frontend tab value
 * @returns {string} Display text (e.g., 'month', 'year', '2 years')
 */
export function getBillingPeriodDisplayText(period) {
  return billingPeriodDisplayText[period] || 'month';
}

/**
 * Get suffix text for a billing period (for credit displays)
 * @param {string} period - Frontend tab value
 * @returns {string} Suffix text (e.g., '/mo', '/yr')
 */
export function getBillingPeriodSuffix(period) {
  return billingPeriodSuffix[period] || '/mo';
}

/**
 * Get "Billed" label text for a billing period
 * @param {string} period - Display text from getBillingPeriodDisplayText
 * @returns {string} Label text (e.g., 'Billed Monthly', 'Billed Annually')
 */
export function getBilledLabel(period) {
  return billedLabelMap[period] || billedV3LabelMap[period];
}

export const billedV3LabelMap = {
  monthly: 'Billed Monthly',
  quarterly: 'Billed Quarterly',
  semi_annual: 'Billed Semi-Annually',
  annual: 'Billed Annually',
  two_year: 'Billed Every 2 Years',
};
/**
 * Get "BilledV3" label text for a billing period
 * @param {string} period - Display text from getBillingPeriodDisplayText
 * @returns {string} Label text (e.g., 'Billed Monthly', 'Billed Annually')
 */
export function getBilledV3Label(period) {
  return billedV3LabelMap[period] || billedLabelMap[period];
}

/**
 * Get frequency text for a billing period (e.g., 'yearly', 'quarterly', 'monthly')
 * @param {string} period - Frontend tab value
 * @returns {string} Frequency text (e.g., 'monthly', 'yearly')
 */
export function getBillingPeriodFrequencyText(period) {
  return billingPeriodFrequencyText[period] || 'monthly';
}
